import { useState, useRef, ChangeEventHandler, useMemo } from 'react'
import { Icon, SlimButton } from 'components'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import * as S from './styles'

type onChangeType = ChangeEventHandler<HTMLTextAreaElement>

export type InputMenuProps = {
  label: string
  placeholder?: string
  value: string
  onChange: onChangeType
  clearAction?: () => void
  searchAction?: () => void
  disabled?: boolean
  hasBorder?: boolean
  fullWidth?: boolean
}

export const InputMenu: React.FC<InputMenuProps> = ({
  label,
  placeholder = 'Buscar',
  value,
  onChange,
  clearAction,
  searchAction,
  disabled,
  hasBorder = true,
  fullWidth = false
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setTimeout(focusInput, 100)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'multiselect-popover' : undefined

  const focusInput = () => {
    inputRef.current?.focus()
  }

  const labelComplement = useMemo(() => {
    if (!value) return ''
    const batch = value.split('\n').filter((item) => item.trim() !== '')
    if (!batch.length) return ''
    return ` •  ${batch[0]} ${batch.length > 1 ? `(+${batch.length - 1})` : ''}`
  }, [value])

  return (
    <S.Wrapper fullWidth={fullWidth}>
      <S.StyledButton
        id={`input-menu-${label}`}
        aria-describedby={id}
        open={open}
        variant="contained"
        onClick={handleClick}
        disableElevation
        endIcon={<KeyboardArrowDownIcon color="primary" />}
        disabled={disabled}
        hasBorder={hasBorder}
        fullWidth={fullWidth}
      >
        {label}
        {labelComplement}
      </S.StyledButton>
      <S.StyledPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        elevation={0}
      >
        <S.PopoverContent>
          <S.InputWrapper>
            <S.InputContainer onClick={focusInput}>
              <textarea
                id="menu-area-input"
                ref={inputRef}
                value={value}
                placeholder={placeholder}
                onChange={onChange}
              />
            </S.InputContainer>
          </S.InputWrapper>

          <S.ActionsWrapper>
            {clearAction && (
              <SlimButton
                id="input-menu-clear-button"
                variant="secondary"
                onClick={clearAction}
                type="button"
                icon={<Icon icon="clear-all" size="20" />}
                disabled={!value}
              />
            )}
            <SlimButton
              id="input-menu-save-button"
              variant="secondary"
              onClick={handleClose}
              type="button"
              icon={<Icon icon="save" size="20" />}
            />
            {searchAction && (
              <SlimButton
                id="input-menu-search-button"
                variant="primary"
                onClick={searchAction}
                type="button"
                content="Buscar"
              />
            )}
          </S.ActionsWrapper>
        </S.PopoverContent>
      </S.StyledPopover>
    </S.Wrapper>
  )
}
